import React, { useEffect, useState } from 'react';
import apiClient from '../../services/api'; // Adjust path if needed
import { Category, Goal } from '../../types'; // Adjust path if needed
import GoalTree from '../../components/goal_planner/GoalTree';
import GoalForm from '../../components/goal_planner/GoalForm';
import { Button } from '../../components/ui/button';
import { PlusCircle } from 'lucide-react';
import { isPast, isThisWeek, isThisMonth, isThisYear, parseISO } from 'date-fns'; // Import date-fns functions

const GoalsPage: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]); // Needed for GoalTree/Form
  const [goals, setGoals] = useState<Goal[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeGoalFilter, setActiveGoalFilter] = useState<string>('active');
  const [isGoalModalOpen, setIsGoalModalOpen] = useState(false);
  const [goalToEdit, setGoalToEdit] = useState<Goal | null>(null);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Fetch Goals and Categories
      const [goalRes, catRes] = await Promise.all([
        apiClient.get('/goals/goals/'),
        apiClient.get('/goals/categories/') // Needed for GoalTree/Form
      ]);
      setGoals(goalRes.data);
      setCategories(catRes.data);
      console.log("Fetched Goals:", goalRes.data);
      console.log("Fetched Categories for Goals page:", catRes.data);
    } catch (err: any) {
      console.error("Failed to fetch goals page data:", err);
      setError(err.message || 'Failed to load goals data.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const refetchGoals = async () => {
      await fetchData();
  };

  const handleToggleGoalCompletion = async (goalId: number, currentCompletedAt: string | null) => {
     const newValue = currentCompletedAt ? null : new Date().toISOString(); // Set to now() or null
     
     try {
        await apiClient.patch(`/goals/goals/${goalId}/`, { completed_at: newValue });
        await refetchGoals(); // Refetch to get definitive state from backend
     } catch (err: any) {
       console.error("Failed to update goal status:", err);
       let errorMessage = "Failed to update goal status.";
       if (err.response?.status === 400 && err.response?.data?.detail) {
           // Use the specific error message from the backend if available
           errorMessage = err.response.data.detail;
       } else if (err.response?.data) {
         // Fallback for other potential DRF validation errors
         errorMessage = JSON.stringify(err.response.data);
       }
       setError(errorMessage); // Show error in the main error display for now
     }
  };

  const handleGoalFilterChange = (filter: string) => {
    setActiveGoalFilter(filter);
  };

  const filteredGoals = goals.filter(goal => {
    const isCompleted = goal.completed_at !== null && goal.completed_at !== undefined;
    const deadline = goal.deadline ? parseISO(goal.deadline) : null; // Parse deadline string
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize today for comparisons

    // --- Filtering Logic --- 
    
    // Handle status filters first
    if (activeGoalFilter === 'completed') return isCompleted;
    if (activeGoalFilter === 'active')    return !isCompleted;
    if (activeGoalFilter === 'all')       return true; 

    // Handle category filter
    if (activeGoalFilter.startsWith('category:')) {
      const categoryId = activeGoalFilter.split(':')[1];
      return goal.category?.toString() === categoryId;
    }

    // --- Handle Date Filters --- 
    // At this point, the filter must be a date filter.
    // Goals must be active and have a valid deadline to match any date filter.
    if (isCompleted || !(deadline instanceof Date)) { 
        return false; // Cannot match any date filter
    }

    // Now we know deadline is a valid Date object and goal is active
    switch (activeGoalFilter) {
      case 'overdue':   return deadline < today;
      case 'due_week':  return isThisWeek(deadline, { weekStartsOn: 1 });
      case 'due_month': return isThisMonth(deadline);
      case 'due_year':  return isThisYear(deadline);
      default:
          // Should not be reached if all filters are handled above,
          // but default to false (doesn't match unknown date filter)
          return false; 
    }
  });

  const handleAddGoal = () => {
    setGoalToEdit(null);
    setIsGoalModalOpen(true);
  };

  const handleEditGoal = (goal: Goal) => {
    setGoalToEdit(goal);
    setIsGoalModalOpen(true);
  };

  const clearGoalToEdit = () => setGoalToEdit(null);

  return (
    <div className="container mx-auto p-4 md:p-6 space-y-6">
      {/* Loading State */} 
      {isLoading && (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          <span className="ml-4 text-muted-foreground">Loading goals...</span>
        </div>
      )}
      
      {/* Error State */} 
      {error && (
        <div className="bg-destructive/10 text-destructive p-4 rounded-md border border-destructive/30">
          <p><strong>Error:</strong> {error}</p>
        </div>
      )}

      {!isLoading && !error && (
        <div className="space-y-4 md:space-y-6">
            <div className="flex justify-between items-center">
              <h1 className="text-xl md:text-2xl font-semibold">Goals</h1>
              <Button onClick={handleAddGoal} size="sm" className="flex items-center gap-2">
                <PlusCircle className="h-4 w-4" />
                <span>Add New Goal</span>
              </Button>
            </div>
            <GoalTree 
                goals={filteredGoals} 
                allGoals={goals} 
                categories={categories}
                onEditGoal={handleEditGoal} 
                onFilterChange={handleGoalFilterChange}
                activeFilter={activeGoalFilter} 
             />
        </div>
      )}

       {/* Goal Modal */} 
       <GoalForm 
        isOpen={isGoalModalOpen}
        setIsOpen={setIsGoalModalOpen}
        goal={goalToEdit}
        categories={categories} 
        parentGoals={goals}
        refreshGoals={refetchGoals}
        clearGoalToEdit={clearGoalToEdit}
      />
    </div>
  );
};

export default GoalsPage; 